import { REVISION, DEPLOY_ENV, ENABLE_DATADOG_ERROR_TRACKING, IS_BROWSER, CORE_API_ORIGIN, CORE_WEB_ORIGIN, GQL_GATEWAY_BASE_URL } from '@reverbdotcom/env';
import { datadogRum } from '@datadog/browser-rum-slim';
import { frontendCurrentUser } from '@reverbdotcom/discovery-ui/src/components/parse_meta';
import { createErrorEventBeforeSend } from '@reverbdotcom/commons/src/errorTracking/errorEventFilter';
import { isLikelyBot } from '@reverbdotcom/commons/src/errorTracking/botDetection';
import { ERROR_EVENT_FILTER_CONFIG, BOT_USER_AGENT_PATTERNS } from '@reverbdotcom/commons/src/errorTracking/errorTrackingConfig';

const DD_ERROR_TRACKING_SERVICE_NAME = 'reverb-ui-client';

let datadogWasInitialized = false;

/**
 * Initializes Datadog's browser SDK for error tracking.
 * Runs as early as possible, before AppInitializer,
 * to be able to detect errors that happen during initialization.
 **/
export function initializeDatadogErrorTracking() {
  try {
    if (datadogWasInitialized) { return; }

    const userConfig = frontendCurrentUser();
    if (!userConfig) { return; }

    const shouldEnableErrorTracking =
      ENABLE_DATADOG_ERROR_TRACKING &&
      IS_BROWSER && // Necessary check because the server bundle used by RRE imports reverb-ui/src/vendor.js.
      // Skip RUM entirely for self-identifying automation. Bots are a large
      // share of traffic at sessionSampleRate: 100, and they generate most of
      // our error-tracking noise (e.g. injected `googletag is not defined`).
      !isLikelyBot({ userAgent: navigator.userAgent, webdriver: navigator.webdriver }, BOT_USER_AGENT_PATTERNS);
    if (!shouldEnableErrorTracking) { return; }

    datadogRum.init({
      applicationId: 'bc24fb80-5c5e-4fc7-9fb0-36e8013fa578',
      clientToken: 'puba2506fc401d2b705950822885a31b154',
      site: 'datadoghq.com',
      service: DD_ERROR_TRACKING_SERVICE_NAME,
      env: DEPLOY_ENV,
      version: REVISION,
      sessionSampleRate: 100, // the not expensive part of RUM, for error reporting
      sessionReplaySampleRate: 0, // the expensive part of RUM that we do not want to pay for
      traceSampleRate: 0, // I don't think we need frontend traces?
      traceContextInjection: 'sampled', // ensure backend service trace sample rates are applied
      allowedTracingUrls: [
        CORE_API_ORIGIN,
        CORE_WEB_ORIGIN,
        GQL_GATEWAY_BASE_URL,
      ],
      allowedGraphQlUrls: [
        GQL_GATEWAY_BASE_URL,
      ],
      // Datadog's RUM SDK has no declarative allowUrls/denyUrls/ignoreErrors;
      // beforeSend is the only filtering hook. We inject a version-controlled
      // origin allowlist + message ignore-list (see errorTrackingConfig.ts).
      // Returning false discards the event before it is sent, so filtered
      // events are never ingested or billed.
      beforeSend: createErrorEventBeforeSend(ERROR_EVENT_FILTER_CONFIG),
    });

    const { user } = userConfig;
    // These attributes populate the `@usr` event properties in Datadog's error UI.
    // Example: `request_id: "123"` turns into `@usr.request_id:"123"`
    datadogRum.setUser({
      id: user.id,
      admin: user.isAdmin,
      context_id: user.cookieId,
      session_id: user.sessionId,
      request_id: user.requestId,
    });

    datadogWasInitialized = true;
  } catch (e) {
    console.error('error initializing datadog:', e);
  }
}
